import React, { useEffect } from "react";
import {
  Modal,
  Form,
  Button,
  List,
  notification,
  Popconfirm,
  Skeleton,
  Steps,
  Tag,
  Select,
  Switch,
  Descriptions,
} from "antd";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import {
  fetchRFI,
  updateRFI,
  postResponse,
  deleteRFIResponse,
} from "../services/rfisAPI";
import { formatDate, formatDateTimeLocal } from "../utils/dateUtils";
import { sanitizeHtml } from "../utils/textUtils";
import AttachmentsView from "./AttachmentsView";
import AttachmentsSection from "./AttachmentsSection";
import { DeleteOutlined } from "@ant-design/icons";

import SimpleRichTextEditor from "./SimpleRichTextEditor";

const PROJECTS_REQUIRING_RFI_RESPONSE_ATTACHMENTS = [
  "6973ec29e593a4a75872d133",
];

const toTimelineTime = (value) => {
  const time = new Date(value || 0).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const describeBallInCourtChange = (change) => {
  const names =
    (change?.ball_in_court_users || [])
      .map((user) => user?.username)
      .filter(Boolean)
      .join(", ") || "N/A";
  const by = change?.changed_by?.username ? ` by ${change.changed_by.username}` : "";
  if ((change?.ball_in_court_ids || []).length === 0) {
    return `Ball in court cleared${by}`;
  }
  if ((change?.previous_ball_in_court_ids || []).length === 0) {
    return `Ball in court set to ${names}${by}`;
  }
  return `Ball in court changed to ${names}${by}`;
};

const ViewRFIModal = ({
  visible,
  onClose,
  rfiId,
  isLocked = false,
  projectInformation = null,
}) => {
  const { currentUser, selectedProject } = useAuth();
  const effectiveProject =
    projectInformation != null ? projectInformation : selectedProject;
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const { data: rfi, refetch } = useQuery({
    queryKey: ["rfi", rfiId],
    queryFn: () => fetchRFI(rfiId),
    enabled: !!rfiId,
  });

  const userRole = effectiveProject?.role;
  const isOwnerOrManager =
    userRole === "owner" || userRole === "project_manager";
  // Field can create/update RFIs (including status) like owner/PM.
  const canCreateOrUpdateRFI = isOwnerOrManager || userRole === "field";
  const isParallelWorkflow =
    effectiveProject?.rfi_workflow_type === "Parallel";
  const requiresResponseAttachment =
    PROJECTS_REQUIRING_RFI_RESPONSE_ATTACHMENTS.includes(
      effectiveProject?.project_id,
    );

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) => updateRFI(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rfi", rfiId] });
      queryClient.invalidateQueries({ queryKey: ["rfis"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-open-rfis"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      notification.success({ message: "Status updated successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update status",
        description: error.response?.data?.detail || error.message,
      });
    },
  });

  const handleRfiStatusChange = (newStatus) => {
    if (!rfi?.id) return;
    updateStatusMutation.mutate({ id: rfi.id, status: newStatus });
  };

  useEffect(() => {
    if (visible) {
      refetch();
    }
  }, [visible, refetch]);

  const isAssignee = rfi?.assignees?.some(
    (assignee) => assignee._id === currentUser.id,
  );
  const isRFIManager = rfi?.rfi_manager?._id === currentUser.id;
  const canRespondToRFI = isAssignee || isRFIManager || isOwnerOrManager;

  const mutation = useMutation({
    mutationFn: ({ rfiId, response }) => postResponse(rfiId, response),
    onSuccess: () => {
      queryClient.invalidateQueries(["rfi", rfiId]);
      notification.success({ message: "Response added successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to add response",
        description: error.message,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ rfiId, responseId }) => deleteRFIResponse(rfiId, responseId),
    onSuccess: () => {
      queryClient.invalidateQueries(["rfi", rfiId]);
      notification.success({ message: "Response deleted successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to delete response",
        description: error.message,
      });
    },
  });

  const returnBallToAssigneesMutation = useMutation({
    mutationFn: ({ id, ballInCourtIds }) =>
      updateRFI(id, { ball_in_court_ids: ballInCourtIds }, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rfi", rfiId] });
      queryClient.invalidateQueries({ queryKey: ["rfis"] });
      notification.success({
        message: "Ball in court returned to assignees",
      });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update ball in court",
        description: error.response?.data?.detail || error.message,
      });
    },
  });

  const handleAddResponse = (values) => {
    const response = {
      response: values.response,
      responded_by: currentUser.id,
      attachments: values.attachments || [],
    };

    if (showAssigneeBallSwitch) {
      response.transfer_ball_to_rfi_manager =
        values.transfer_ball_to_rfi_manager !== false;
    }

    mutation.mutate({ rfiId, response });
    form.resetFields();
    form.setFieldsValue({ transfer_ball_to_rfi_manager: true });
  };

  const handleClose = () => {
    form.resetFields();
    form.setFieldsValue({ transfer_ball_to_rfi_manager: true });
    onClose();
  };

  const isUserInBallInCourt = rfi?.ball_in_court_ids?.some(
    (id) => String(id) === String(currentUser.id),
  );
  const isBallInCourtEmpty = (rfi?.ball_in_court_ids || []).length === 0;
  const isOnlyUserInBallInCourt =
    rfi?.ball_in_court_ids?.length === 1 && isUserInBallInCourt;
  const isOnlyRFIManagerInBallInCourt =
    rfi?.ball_in_court_ids?.length === 1 &&
    String(rfi.ball_in_court_ids[0]) === String(rfi?.rfi_manager?._id);

  const showAssigneeBallSwitch =
    isParallelWorkflow &&
    isAssignee &&
    !isRFIManager &&
    isUserInBallInCourt &&
    rfi?.status === "Open";

  const showManagerReturnBallButton =
    isParallelWorkflow &&
    (isRFIManager || isOwnerOrManager) &&
    (isOnlyUserInBallInCourt ||
      isOnlyRFIManagerInBallInCourt ||
      isBallInCourtEmpty) &&
    rfi?.status === "Open";

  const handleDeleteResponse = (responseId) => {
    deleteMutation.mutate({ rfiId, responseId });
  };

  const responseTimeline = [
    ...(rfi?.responses || []).map((response) => ({
      response,
      time: toTimelineTime(response.responded_on),
    })),
    ...(rfi?.ball_in_court_history || []).map((change) => ({
      change,
      time: toTimelineTime(change.changed_on),
    })),
  ].sort((a, b) => a.time - b.time);

  // Helper function to get current step number
  const getCurrentStepNumber = () => {
    if (!rfi?.steps || rfi.steps.length === 0) {
      return 0;
    }

    // Find the first incomplete step
    for (let i = 0; i < rfi.steps.length; i++) {
      if (!isStepCompleted(i)) {
        return i;
      }
    }

    // If all steps are completed, return -1 to indicate completion
    return -1;
  };

  // Helper function to check if a step is completed
  const isStepCompleted = (stepIndex) => {
    if (!rfi?.steps || !rfi?.responses) return false;

    const stepUserIds = rfi.steps[stepIndex].map((user) => user._id || user);
    const respondedUserIds = new Set(
      rfi.responses
        .map((response) => response.responded_by._id || response.responded_by)
        .filter((userId) => stepUserIds.includes(userId)),
    );

    // If rfi_sequential_requires_all is true, all users in the step must respond
    if (effectiveProject?.rfi_sequential_requires_all === true) {
      return stepUserIds.every((userId) => respondedUserIds.has(userId));
    }

    // If rfi_sequential_requires_all is false, any user responding completes the step
    return respondedUserIds.size > 0;
  };

  // Helper function to get step status
  const getStepStatus = (stepIndex) => {
    if (!rfi?.steps) return "wait";

    const currentStep = getCurrentStepNumber();

    if (currentStep === -1) {
      // All steps completed
      return "finish";
    }

    if (stepIndex < currentStep) return "finish";
    if (stepIndex === currentStep) return "process";
    return "wait";
  };

  return (
    <Modal
      title={rfi?.number != null ? `RFI #${rfi.number}` : "RFI"}
      open={visible}
      onCancel={isLocked ? undefined : handleClose}
      footer={null}
      width={800}
      closable={!isLocked}
      maskClosable={!isLocked}
      keyboard={!isLocked}
      styles={
        isLocked
          ? { mask: { backgroundColor: "rgba(179, 179, 179, 0.83)" } }
          : undefined
      }
    >
      {!rfi ? (
        <Skeleton active />
      ) : (
        <>
          <Descriptions
            bordered
            column={2}
            size="small"
            labelStyle={{ width: "9rem" }}
          >
            <Descriptions.Item label="Title" span={2}>
              {rfi.title}
            </Descriptions.Item>
            <Descriptions.Item label="Question" span={2}>
              <div
                className="max-h-[240px] overflow-y-auto [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:list-inside [&_a]:text-blue-600 [&_a]:underline"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(rfi.question || ""),
                }}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {canCreateOrUpdateRFI ? (
                <Select
                  value={rfi.status}
                  onChange={handleRfiStatusChange}
                  className="status-select-tag w-[120px]"
                  loading={updateStatusMutation.isPending}
                  disabled={updateStatusMutation.isPending}
                  popupRender={(menu) => menu}
                  suffixIcon={
                    <span className="text-xs text-gray-400 ml-1">▼</span>
                  }
                >
                  <Select.Option value="Open">
                    <Tag color="orange" className="m-0 w-full text-center block">
                      Open
                    </Tag>
                  </Select.Option>
                  <Select.Option value="Closed">
                    <Tag
                      color="default"
                      className="m-0 w-full text-center block"
                    >
                      Closed
                    </Tag>
                  </Select.Option>
                  <Select.Option value="Draft">
                    <Tag color="blue" className="m-0 w-full text-center block">
                      Draft
                    </Tag>
                  </Select.Option>
                </Select>
              ) : (
                <Tag
                  color={
                    {
                      Open: "orange",
                      Closed: "default",
                      Draft: "blue",
                    }[rfi.status] || "default"
                  }
                  className="m-0"
                >
                  {rfi.status}
                </Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Due Date">
              {rfi.due_date ? formatDate(rfi.due_date) : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="RFI Manager">
              {rfi.rfi_manager?.username || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Created By">
              {rfi.created_by || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Assignees" span={2}>
              {(rfi.assignees || []).map((a) => a.username).join(", ") || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Ball in Court" span={2}>
              {(rfi.ball_in_court_users || [])
                .map((user) => user.username)
                .join(", ") || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Cc" span={2}>
              {(rfi.distribution_list_details || [])
                .map((a) => a.username)
                .join(", ") || "N/A"}
            </Descriptions.Item>
            {effectiveProject?.rfi_workflow_type === "Sequential" &&
              rfi.steps?.length > 0 && (
                <Descriptions.Item label="Review Order" span={2}>
                  <Steps
                    direction="vertical"
                    size="small"
                    current={
                      getCurrentStepNumber() === -1
                        ? undefined
                        : getCurrentStepNumber()
                    }
                  >
                    {rfi.steps.map((step, stepIndex) => {
                      const stepUsers = step.map((user) => {
                        if (user.username) return user.username;
                        const userDetails = rfi.assignees?.find(
                          (assignee) =>
                            assignee._id === user || assignee.id === user,
                        );
                        return userDetails ? userDetails.username : user;
                      });
                      const isCompleted = isStepCompleted(stepIndex);
                      const stepStatus = getStepStatus(stepIndex);

                      return (
                        <Steps.Step
                          key={stepIndex}
                          title={`Step ${stepIndex + 1}`}
                          description={
                            <div className="mt-2">
                              <div className="mb-2">
                                <strong>Reviewers:</strong>{" "}
                                {stepUsers.join(", ")}
                              </div>
                              <div className="flex items-center gap-2">
                                <Tag
                                  color={
                                    isCompleted
                                      ? "green"
                                      : stepStatus === "process"
                                        ? "blue"
                                        : "default"
                                  }
                                >
                                  {isCompleted
                                    ? "Completed"
                                    : stepStatus === "process"
                                      ? "In Progress"
                                      : "Pending"}
                                </Tag>
                                {stepStatus === "process" && (
                                  <Tag color="orange">Ball in Court</Tag>
                                )}
                              </div>
                            </div>
                          }
                          status={stepStatus}
                        />
                      );
                    })}
                  </Steps>
                </Descriptions.Item>
              )}
            {rfi.linked_drawings?.length > 0 && (
              <Descriptions.Item label="Linked Drawings" span={2}>
                <List
                  size="small"
                  dataSource={rfi.linked_drawings}
                  renderItem={(drawing) => {
                    const label =
                      drawing.display_label ||
                      [
                        drawing.drawing_number
                          ? `Drawing ${drawing.drawing_number}`
                          : null,
                        drawing.title,
                        drawing.revision_title
                          ? `Rev.${drawing.revision_title}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(": ") ||
                      "Drawing";
                    const canOpen =
                      drawing.drawing_folder_id && drawing.drawing_id;
                    return (
                      <List.Item className="!px-0">
                        {canOpen ? (
                          <Link
                            to={`/drawings/${drawing.drawing_folder_id}/${drawing.drawing_id}`}
                          >
                            {label}
                          </Link>
                        ) : (
                          <span>{label}</span>
                        )}
                      </List.Item>
                    );
                  }}
                />
              </Descriptions.Item>
            )}
            {rfi.attachments?.length > 0 && (
              <Descriptions.Item label="Attachments" span={2}>
                <AttachmentsView attachments={rfi.attachments} canDelete={false} />
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Responses" span={2}>
              <List
                dataSource={responseTimeline}
                locale={{ emptyText: "No responses yet" }}
                renderItem={({ response: item, change }) =>
                  change ? (
                    <List.Item className="!px-0">
                      <div className="w-full text-gray-500 text-sm">
                        <div>{describeBallInCourtChange(change)}</div>
                        <p className="mt-1 mb-0">
                          {formatDateTimeLocal(change.changed_on)}
                        </p>
                      </div>
                    </List.Item>
                  ) : (
                    <List.Item
                      className="!px-0"
                      actions={[
                        false && (
                          <Popconfirm
                            title="Are you sure you want to delete this response?"
                            onConfirm={() => handleDeleteResponse(item.id)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              loading={deleteMutation.isPending}
                            />
                          </Popconfirm>
                        ),
                      ].filter(Boolean)}
                    >
                      <div className="w-full">
                        <div className="mb-2">
                          <strong>{item.responded_by?.username || "User"}:</strong>
                          <div
                            className="mt-1 max-w-full [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                            dangerouslySetInnerHTML={{
                              __html: sanitizeHtml(item.response),
                            }}
                          />
                        </div>
                        {item.attachments?.length > 0 && (
                          <div className="mt-2">
                            <AttachmentsView
                              attachments={item.attachments}
                              canDelete={false}
                            />
                          </div>
                        )}
                        <p className="text-gray-500 text-sm mt-2 mb-0">
                          {formatDateTimeLocal(item.responded_on)}
                        </p>
                      </div>
                    </List.Item>
                  )
                }
              />
            </Descriptions.Item>
          </Descriptions>

          {canRespondToRFI && (
            <Form
              form={form}
              onFinish={handleAddResponse}
              layout="vertical"
              className="mt-4"
            >
              <Form.Item
                label="Add Response"
                name="response"
                rules={[
                  { required: true, message: "Please input your response!" },
                ]}
              >
                <SimpleRichTextEditor
                  placeholder="Enter your response..."
                  aiEnhanceTarget="response"
                />
              </Form.Item>

              <Form.Item
                name="attachments"
                label="Attachments"
                rules={
                  requiresResponseAttachment
                    ? [
                        {
                          validator: (_, value) =>
                            Array.isArray(value) && value.length > 0
                              ? Promise.resolve()
                              : Promise.reject(
                                  new Error(
                                    "Please add at least one attachment before submitting the response.",
                                  ),
                                ),
                        },
                      ]
                    : undefined
                }
              >
                <AttachmentsSection
                  projectId={rfi.project_id}
                  label="Add Response Attachments"
                  title="Upload Response Documents"
                  description="Upload documents for your response"
                />
              </Form.Item>

              {showAssigneeBallSwitch && (
                <Form.Item className="mb-4">
                  <div className="flex items-center gap-2">
                    <Form.Item
                      name="transfer_ball_to_rfi_manager"
                      valuePropName="checked"
                      initialValue={true}
                      noStyle
                    >
                      <Switch />
                    </Form.Item>
                    <span>Change Ball in Court to RFI Manager</span>
                  </div>
                </Form.Item>
              )}

              <Form.Item>
                <div className="flex items-center gap-2">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={mutation.isPending}
                  >
                    Submit Response
                  </Button>
                  {showManagerReturnBallButton && (
                    <Button
                      type="default"
                      loading={returnBallToAssigneesMutation.isPending}
                      onClick={() =>
                        returnBallToAssigneesMutation.mutate({
                          id: rfi.id,
                          ballInCourtIds: (rfi.assignees || []).map(
                            (a) => a._id || a.id,
                          ),
                        })
                      }
                    >
                      Change Ball in Court to Assignees
                    </Button>
                  )}
                </div>
              </Form.Item>
            </Form>
          )}
        </>
      )}
    </Modal>
  );
};

export default ViewRFIModal;
